import { useState, useEffect } from "react";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Family, FamilySettings, Cashout, Kid } from "@starquest/core";
import styles from "./BankSettings.module.css";

const PRESETS = [
  { stars: 5,   cash: 1 },
  { stars: 10,  cash: 1 },
  { stars: 25,  cash: 1 },
  { stars: 50,  cash: 5 },
  { stars: 100, cash: 10 },
];

export default function BankSettings() {
  const { familyId } = useAuthStore();
  const { family, kids, cashouts, setFamily, setKids, setCashouts } = useFamilyStore();
  const theme = useTheme();

  const [stars, setStars] = useState(family?.settings.pointRate   ?? 10);
  const [cash,  setCash]  = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!familyId) return;
    api.getFamily(familyId).then((f) => {
      const fam = f as Family;
      setFamily(fam);
      setStars(fam.settings.pointRate);
      setCash(1);
    });
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
    api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[]));
  }, [familyId]);

  async function saveRate() {
    if (!familyId || !family) return;
    setSaving(true);
    const newPointRate = Math.round(stars / cash);
    const settings: FamilySettings = { ...family.settings, pointRate: newPointRate };
    await api.updateFamily(familyId, { settings });
    api.getFamily(familyId).then((f) => {
      const fam = f as Family;
      setFamily(fam);
      setStars(fam.settings.pointRate);
    });
    setSaving(false);
  }

  async function reviewCashout(cashoutId: string, action: "approve" | "decline") {
    if (!familyId) return;
    await api.reviewCashout(familyId, cashoutId, action);
    api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[]));
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  const pendingCashouts = cashouts.filter((c) => c.status === "pending");

  return (
    <div className={styles.page}>
      <ParentSidebar />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>💵 {theme.pointName} → Cash Rate</h1>
            <div className={styles.sub}>Set how many {theme.pointNamePlural.toLowerCase()} convert to a real dollar. Only you can change this.</div>
          </div>
        </div>

        <div className={styles.grid}>
          <div>
            <div className={styles.rateCard}>
              <div className={styles.rateLabel}>Current exchange rate</div>
              <div className={styles.rateDisplay}>
                <div className={styles.rateBlock}>
                  <div className={`${styles.rateNum} ${styles.gold}`}>{stars}</div>
                  <div className={styles.rateUnit}>{theme.pointEmoji} {theme.pointNamePlural}</div>
                </div>
                <div className={styles.rateEq}>=</div>
                <div className={styles.rateBlock}>
                  <div className={`${styles.rateNum} ${styles.green}`}>${cash}</div>
                  <div className={styles.rateUnit}>USD</div>
                </div>
              </div>

              <div className={styles.rateCtrls}>
                <div className={styles.rateCtrl}>
                  <label>{theme.pointNamePlural} required</label>
                  <div className={styles.inp}>
                    <input type="number" value={stars} min={1}
                      onChange={(e) => setStars(+e.target.value || 1)} />
                    <span>{theme.pointEmoji}</span>
                  </div>
                </div>
                <div className={styles.rateCtrl}>
                  <label>Equals dollars</label>
                  <div className={styles.inp}>
                    <span>$</span>
                    <input type="number" value={cash} min={0.01} step={0.25}
                      onChange={(e) => setCash(+e.target.value || 1)} />
                  </div>
                </div>
              </div>

              <div className={styles.presets}>
                <span className={styles.presetLabel}>Presets:</span>
                {PRESETS.map((p) => (
                  <button
                    key={`${p.stars}-${p.cash}`}
                    className={`${styles.preset} ${stars === p.stars && cash === p.cash ? styles.presetActive : ""}`}
                    onClick={() => { setStars(p.stars); setCash(p.cash); }}
                  >
                    {p.stars}{theme.pointEmoji} = ${p.cash}
                  </button>
                ))}
              </div>

              <button className={styles.saveBtn} onClick={saveRate} disabled={saving}>
                {saving ? "Saving…" : "Save Rate"}
              </button>
            </div>
          </div>

          <div>
            <div className={styles.pendingCard}>
              <h3 className={styles.pendingTitle}>
                📮 Cash-out Requests
                {pendingCashouts.length > 0 && <span className={styles.badge}>{pendingCashouts.length}</span>}
              </h3>
              {pendingCashouts.length === 0 && <p className={styles.empty}>No pending requests</p>}
              {pendingCashouts.map((co) => {
                const kid = kids.find((k) => k.kidId === co.kidId);
                return (
                  <div key={co.cashoutId} className={styles.prRow}>
                    <div className={styles.prAv}>{kid?.avatar ?? "🧑‍🚀"}</div>
                    <div className={styles.prInfo}>
                      <div className={styles.prName}>{kid?.name ?? co.kidId}</div>
                      <div className={styles.prMeta}>{co.points} {theme.pointEmoji} → ${co.dollars}</div>
                    </div>
                    <div className={styles.prAmt}>
                      <div className={styles.prDollars}>${co.dollars}</div>
                      <div className={styles.prStars}>{theme.pointEmoji} {co.points}</div>
                    </div>
                    <div className={styles.prBtns}>
                      <button className={`${styles.vbtn} ${styles.ok}`} onClick={() => reviewCashout(co.cashoutId, "approve")}>✓ Pay</button>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => reviewCashout(co.cashoutId, "decline")}>✗</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
